import { useMemo } from 'react';
import { Box, Typography } from '../../primitives';
import { palette } from '../../theme/palette';
import * as Icons from '../../icons';
import { isAppleShortcutPlatform } from './useCommandPalette';
import { TOPBAR_CONTROL_HEIGHT } from '../../styleUtils';
import { iconSize } from '@munifocus/tokens';

export interface CommandPaletteTriggerProps {
  onClick: () => void;
  /** Visible placeholder, e.g. "Search". Also the button's accessible name. */
  label: string;
  /** `⌘K` / `Ctrl K`, from `useCommandPalette().shortcutLabel`. */
  shortcutLabel: string;
  /** Render for the deep-teal chrome (gov/web headers) rather than a light bar. */
  onDark?: boolean;
  /**
   * Breakpoint at which the control grows from an icon button into the full
   * search field. `sm` suits a header whose other content is already gone by
   * then; a header that still carries a brand lockup below `md` (the Government
   * Portal's, on tablets) passes `md` so the field never competes with it for
   * the same row.
   */
  expandAt?: 'sm' | 'md' | 'lg';
}

/**
 * The header affordance for the palette — a fake search field that opens it.
 *
 * A hotkey nobody can see is a feature only its author uses, so the shortcut is
 * printed on the control itself. It is a `<button>`, not an `<input>`: there is
 * exactly one real search field and it lives inside the dialog, and shipping a
 * second focusable text box that silently forwards keystrokes would give screen
 * readers two competing comboboxes for one feature.
 *
 * Below `expandAt` it collapses to an icon-only button — the shortcut is
 * irrelevant on a touch device and the label would crowd the brand lockup.
 */
export function CommandPaletteTrigger({
  onClick,
  label,
  shortcutLabel,
  onDark = false,
  expandAt = 'sm',
}: CommandPaletteTriggerProps) {
  const fg = onDark ? palette.text.onDarkSubtle : palette.text.muted;
  const border = onDark ? palette.border.onDarkSubtle : palette.border.line;
  const hoverBg = onDark ? palette.background.surfaceOnDarkSoft : palette.background.parch;

  // One breakpoint drives every dimension of the collapse, so the box, its label
  // and the shortcut chip can never disagree about which form is on screen.
  const atWidth = <T,>(collapsed: T, expanded: T): Record<string, T> => ({
    xs: collapsed,
    [expandAt]: expanded,
  });

  // Announce the binding that is actually live on this platform — the hook
  // registers Cmd-K on Apple and Ctrl-K everywhere else, never both, so naming
  // both would advertise a shortcut that does nothing here.
  const keyShortcut = useMemo(() => (isAppleShortcutPlatform() ? 'Meta+K' : 'Control+K'), []);

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-keyshortcuts={keyShortcut}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        height: TOPBAR_CONTROL_HEIGHT,
        // The search field is the header's least load-bearing item, so it never
        // takes room from the brand: it holds its own width and shrinks by
        // collapsing at `expandAt`, rather than squeezing its neighbours.
        flexShrink: 0,
        px: atWidth(0, 1.5),
        width: atWidth<number | string>(36, 'auto'),
        minWidth: atWidth<number | string>(36, 200),
        justifyContent: atWidth('center', 'flex-start'),
        borderRadius: 1.5,
        border: `1px solid ${border}`,
        backgroundColor: 'transparent',
        color: fg,
        cursor: 'pointer',
        transition: 'background-color 200ms, border-color 200ms, color 200ms',
        '&:hover': {
          backgroundColor: hoverBg,
          color: onDark ? palette.text.onDark : palette.text.secondary,
        },
        '&:active': { transform: 'scale(0.98)' },
      }}
    >
      <Icons.Search size={iconSize.md} strokeWidth={1.75} aria-hidden style={{ flexShrink: 0 }} />
      <Typography
        component="span"
        sx={{
          display: atWidth('none', 'block'),
          flexGrow: 1,
          textAlign: 'left',
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 13.5,
          color: 'inherit',
        }}
      >
        {label}
      </Typography>
      <Box
        component="kbd"
        aria-hidden
        sx={{
          display: atWidth('none', 'block'),
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 11,
          fontWeight: 600,
          lineHeight: 1.6,
          px: 0.75,
          borderRadius: 0.75,
          border: `1px solid ${border}`,
          color: 'inherit',
          whiteSpace: 'nowrap',
        }}
      >
        {shortcutLabel}
      </Box>
    </Box>
  );
}
